import { crdt } from '@w/crdt'
import { createLiveStaging } from '../db/live-staging'
import { refreshTranslationRows } from '../translation/page-translation-rows'
// INV-2: shutdown cancels and joins initialization; page commits flush before published resources close; cancelled initialization never publishes.
import { instrumentFetch, type Tel } from '@w/tel'
import { disposeAll, safe } from '@w/utils'
import { nativeTongueFor } from '../translation/native-language'
import { createBrowserSearchEmbeddings } from '../search/browser-search-embeddings'
import { movePageCommand } from './page-commands'
import { type MoveInput } from './page-commands'
import { createServerEmbeddings } from '../search/server-embeddings.client'
import type { RuntimeTask } from '@w/runtime'
import { purgePageUnloads } from '../db/page-unload-journal'
import { createSpacesResources, replayPageUnloads } from './spaces-resources'
import { commitPageEdits } from '../db/commit-page-edit'
import { browserSocket, createRealtimeClients, presenceUrl } from './realtime-clients'
import { openBrowserSpace } from './spaces-browser'
import { attachBound, bindCurrentAnonymous, readBindings, type AttachDevice } from './attach-anonymous'
import type { SpaceRuntime } from './spaces-runtime'
import { onVisibilityChange } from '@w/runtime/visibility'
import { createFlashcardsRpcOptions, directoryPersonalRootId, openPersonalWorkspace, refreshAfterDirectoryCommands, refreshWorkspaceDirectory, signOutOnUnauthorized } from './flashcards-rpc-options'
import { createWriteGate, defaultPageCommitPolicy, type PageCommitPolicy } from '../db/page-commit-buffer'
import { createAdoptBumpErrors } from '../lexicon/adopt-bump-errors'
import { createSessionOwner } from '@w/auth-fe'
import { feEnv } from '@w/utils-fe/env'
import { localStorageOrMemory } from '@w/utils-fe'
import { createAuthClient } from '../flashcards-auth'
import { anonymousStartup } from './anonymous-startup'
import { anonymousPersonalRootId, createPrincipalFetch, seedAnonymousDirectory, telemetryPath } from './principal-fetch'
import { getBrowserDeviceId } from '@w/db-turso-browser-adapter/browser-device-id'
import { QueryClient } from '@w/react-query'
import { createRpcClient } from '@w/rpc'
import { feEnvVars } from '../fe-env-vars'
import { createFlashcardsMediaClient } from '../media/flashcards-media.client'
import type { FlashcardsRpcServer } from '../server/rpc/flashcards-rpc.server'
import { systemClock, type FlashcardsFeRouterCtx, type FlashcardsTestSupport } from './flashcards-fe.router-ctx'
import { createStaticFrequencyList } from '../lexicon/vocabulary-progress'
import { createFlashcardsClientTel } from '../telemetry.client'
import { runFlashcardsStartup } from '../flashcards-startup'
import { createDeviceSpeechStore } from '../listen/speech-session'

export const flashcardsClientTel = createFlashcardsClientTel({
  env: feEnv().mode,
})

/**
 * The device's resources, injected only where a test stands in for the browser: storage, the network below the
 * principal fetch, the workspace file runtime and the media store. Every transport is still built here.
 */
export type ProdContextDevice = {
  storage?: Storage
  network?: Window['fetch']
  spaceRuntime?: SpaceRuntime
  media?: Pick<Parameters<typeof createFlashcardsMediaClient>[0], 'driver' | 'metadataDb' | 'imageLoader'>
  testSupport?: FlashcardsTestSupport
}

export function createFlashcardsFeRouterProdContext(options?: {
  pageCommit?: PageCommitPolicy
  device?: ProdContextDevice
}) {
  return runFlashcardsStartup(flashcardsClientTel.current, 'Initialize Flashcards',
    (startupTel, task) => createProdContext(options, task, startupTel))
}

async function createProdContext(options: { pageCommit?: PageCommitPolicy; device?: ProdContextDevice } | undefined, startup: RuntimeTask, startupTel: Tel) {
  const device = options?.device ?? {}
  // Blocked site data throws from the localStorage getter; this load then keeps its state in memory.
  const storage = device.storage ?? localStorageOrMemory()
  const writes = createWriteGate()
  const session = createSessionOwner<ReturnType<typeof createSpacesResources>>({
    reportReleaseError: (error, released) => { flashcardsClientTel.current.error(error, { operation: 'session.release', userId: released.userId }) },
  })
  const queryClient = new QueryClient()
  const tel = flashcardsClientTel.current
  // flashcards-local-first.spec.md PrincipalFetch: auth, rpc, replica and media are all built on this one boundary.
  // `authClient` is declared below; the boundary reads it only per request.
  const network = device.network ?? ((input, init) => fetch(input, init))
  const principalFetch = createPrincipalFetch({ network: (input, init) => {
    const path = new URL(input instanceof Request ? input.url : String(input), location.href).pathname
    if (authClient.isLoggedIn() && !authClient.hasFreshSession() && !path.includes('/auth/') && !telemetryPath.test(path)) {
      return Promise.reject(new TypeError('Session unavailable offline'))
    }
    return network(input, init)
  }, principal: () => authClient.getUserOrNull(), storage })
  flashcardsClientTel.bindTransport(principalFetch)
  const authClient = createAuthClient({
    tel,
    baseUrl: feEnvVars.BACKEND_URL,
    fetch: principalFetch,
    // Blocked site data: the signed-in user then lives in the per-load memory storage, so a sign-in holds for this load.
    storage,
    anonymous: anonymousStartup,
  })
  // flashcards-local-first.spec.md: in a browser, no stored account means the device's anonymous user and no session
  // check; an account returns only through an explicit login.
  await authClient.ensurePrincipal()
  // A stored account is an offline identity. Only a fresh session admits network work.
  if (authClient.isLoggedIn() || !authClient.getUserOrNull()) {
    await startupTel.span({ name: 'app.startup.restoreSession', input: undefined }, restoreSessionTel =>
      startup.run({ name: 'Restore session', kind: 'startup' }, () => authClient.refreshUser(restoreSessionTel)))
  }
  let bucket: ReturnType<typeof createFlashcardsMediaClient> | undefined
  function mediaClient() {
    const principal = authClient.getUser().id
    if (bucket?.principal !== principal) { void bucket?.close(); bucket = undefined }
    bucket ??= createFlashcardsMediaClient({
      principal, isActive: () => !closing && authClient.getUserOrNull()?.id === principal,
      remote: authClient.isLoggedIn(),
      baseUrl: feEnvVars.BACKEND_URL, fetch: principalFetch, ...device.media,
      tel,
    })
    return bucket
  }
  const withCredentials: Window['fetch'] = (input, init) =>
    principalFetch(input, {
      ...init,
      credentials: 'include',
    })
  const fetchFn = instrumentFetch({ tel, mode: 'native', fetch: withCredentials })
  const rpcConfig = { baseUrl: feEnvVars.BACKEND_URL, fetch: signOutOnUnauthorized(fetchFn, authClient, tel), tel: tel }
  const serverEmbeddings = createServerEmbeddings({ baseUrl: feEnvVars.BACKEND_URL, fetch: withCredentials,
    canEmbed: () => authClient.getUserOrNull()?.role === 'admin' })
  const searchEmbeddings = createBrowserSearchEmbeddings({ tel, server: serverEmbeddings })
  const rpcClient = refreshAfterDirectoryCommands(createRpcClient<FlashcardsRpcServer>(rpcConfig), refreshOpenWorkspaces, { storage, userId: () => authClient.getUser().id })
  const rpc = createFlashcardsRpcOptions({
    client: rpcClient,
    queryClient,
  })
  const deviceId = await startupTel.span({ name: 'app.startup.deviceIdentity', input: undefined }, () =>
    startup.run({ name: 'Read device identity', kind: 'startup' }, () => getBrowserDeviceId(storage)))
  let spaces: ReturnType<typeof createSpacesResources> | undefined
  let initializingSpaces: typeof spaces
  let initialization = Promise.resolve()
  function _ensureUserResources(startupTel: Tel) {
    const current = initialization.then(() => initializeUserResources(startupTel))
    initialization = current.catch(() => {})
    return current
  }
  async function initializeUserResources(bootTel: Tel) {
    requireActive()
    const userId = authClient.getUser().id
    if (spaces?.userId === userId) return
    // flashcards-local-first.spec.md AttachBinding: written before any staging, at the principal change.
    if (authClient.isLoggedIn()) bindCurrentAnonymous(storage, userId)
    writes.reopen()
    spaces = undefined
    await session.retire()
    requireActive()
    queryClient.clear()
    seedAnonymousDirectory(storage, authClient.getUser())
    const directory = await refreshWorkspaceDirectory({ queryClient, rpc, storage, userId, tel, bootTel })
    requireActive()
    spaces = createSpacesResources({ get remote() { return authClient.hasFreshSession() }, realtime: realtimeHooks, writes, userId, tel, directoryClient: queryClient, storage, bucket: mediaClient,
      list: async (workspaceId, listTel, signal) => {
        signal.throwIfAborted()
        // Retain last-known permissions before exposing cached content, even on Cards-first visits.
        const levels = await rpcClient.page.levels.query({ workspaceId }, { tel: listTel, signal })
        signal.throwIfAborted()
        // Success only: a refusal stays outside the observed query (spaces-resources refetches it on failure).
        queryClient.setQueryData(rpc.page.levels.queryOptions({ workspaceId }).queryKey, levels)
        const personalRootId = await directoryPersonalRootId({ queryClient, rpc, workspaceId, tel: listTel })
        return [{ id: workspaceId, workspaceId, kind: 'private' as const, ownerUserId: userId, personalRootId, name: 'Pages' }]
      },
      open: (space, openTel, signal) => openBrowserSpace({ onTranslationsWarmed: () => { refreshTranslationRows(queryClient) }, space, signal, userId, deviceId, backendUrl: feEnvVars.BACKEND_URL, fetch: fetchFn, storage, tel, bootTel: openTel, serverEmbeddings,
        translate: async ({ texts, target, format }, tel) => (await rpcClient.translate.mutate({ texts, target, format }, { tel })).translations,
        language: () => nativeTongueFor(authClient.getUser().settings.lexicon?.nativeLanguage, preferredBrowserLanguage()) }, device.spaceRuntime),
      closeReaders: spaceId => { writes.revoke(spaceId); return Promise.resolve() },
    })
    session.open(spaces)
    initializingSpaces = spaces
    try {
      await openPersonalWorkspace(spaces, directory, userId, bootTel)
      requireActive()
      if (authClient.isLoggedIn()) await attachToAccount(spaces, userId)
      const retained = spaces
      void directory.refreshed.then(() => retained.refreshRetained(tel)).catch(error => { tel.warn('Retained workspace reconciliation unavailable', { error }) })
    } finally {
      initializingSpaces = undefined
    }
  }
  /** flashcards-local-first.spec.md Attach: every source bound to this account is carried into its personal workspace.
   * A failure keeps the binding and the source: the next sign-in of the same account resumes it. */
  async function attachToAccount(target: NonNullable<typeof spaces>, userId: string) {
    const attachDevice: AttachDevice = { storage, tel,
      async openSource(source) {
        const file = await openBrowserSpace({ space: { id: source, workspaceId: source, kind: 'private', ownerUserId: source, personalRootId: anonymousPersonalRootId(storage, source), name: 'Pages' }, userId: source, deviceId,
          backendUrl: feEnvVars.BACKEND_URL, fetch: createPrincipalFetch({ network, principal: () => ({ id: source, anonymous: true }), storage }), storage, tel, bootTel: tel }, device.spaceRuntime)
        const driver = safe(file.db.driver, 'INV: an opened space has a driver')
        // Spec Attach step 2: a burst X's page left in the unload journal (a sign-in navigation cut its commit timer) is
        // committed into X's file, as X, before staging.
        try {
          await replayPageUnloads({ storage, userId: source, workspaceId: source, tel,
            commit: (pageId, frame) => commitPageEdits(file.db, [{ pageId, userId: source, now: new Date(), tel, frame }]) })
        } catch (error) { await file.close(); throw error }
        return { query: (sql, params) => driver.query(sql, params), close: () => file.close() }
      },
      commit: (workspaceId, rows, held) => target.importCarriedRows(workspaceId, rows, tel, held),
      personalRootId: workspaceId => target.get(workspaceId).space.personalRootId,
    }
    for (const binding of readBindings(storage).filter(candidate => candidate.target === userId)) {
      try { await attachBound(attachDevice, binding) }
      catch (error) { tel.error(error, { operation: 'local-first.attach', source: binding.source }) }
    }
  }
  function requireActive() {
    if (closing) throw new DOMException('Flashcards context closed', 'AbortError')
  }
  function requireSpaces() {
    if (!spaces || spaces.userId !== authClient.getUser().id) throw new Error('User resources not initialized')
    return spaces
  }
  async function refreshOpenWorkspaces() {
    if (!spaces) return
    await queryClient.invalidateQueries({ queryKey: ['workspace'] })
    // Invalidation already refetches observed queries; load only fetches an unobserved stale directory.
    await queryClient.load(rpc.workspace.list.queryOptions())
    await spaces.refreshRetained(tel)
    // Each open workspace's round publishes page permissions once (spaces-resources triggerWorkspace).
    await spaces.refreshOpen(tel)
  }
  function focus() { void refreshOpenWorkspaces().catch(error => { tel.warn('Space refresh unavailable', { error }) }) }
  window.addEventListener('focus', focus)
  window.addEventListener('online', focus)
  const stopVisibility = onVisibilityChange(visible => { if (visible) focus() })
  const realtime = createRealtimeClients({ user: () => authClient.getUserOrNull(),
    connect: workspaceId => browserSocket(presenceUrl(feEnvVars.BACKEND_URL, workspaceId)),
    storage,
    pending: async (workspaceId, pageId, userId, epoch) => {
      const driver = spaces?.db(workspaceId).driver
      if (!driver) throw new Error('Live workspace is closed')
      const staging = createLiveStaging(driver)
      await staging.retireCovered(pageId)
      return (await staging.read(pageId)).filter(frame => (frame.direction === 'incoming' || frame.userId === userId) && (epoch === undefined || crdt.inspectUpdate(frame.frame).epoch === epoch)).map(frame => ({ bytes: frame.frame, outgoing: frame.direction === 'outgoing' }))
    },
    stage: async (workspaceId, frame) => {
      const driver = spaces?.db(workspaceId).driver
      if (!driver) throw new Error('Live workspace is closed')
      await createLiveStaging(driver).stage(frame.pageId, frame.userId, frame.direction, frame.bytes)
    },
    pull: async workspaceId => { await spaces?.trigger(workspaceId, false, tel) } })
  const realtimeHooks = { opened: realtime.open, closed: realtime.close, pushed: realtime.poke }
  const signOut = authClient.signOut
  authClient.signOut = async () => {
    await initialization
    // Stop producers before the barrier: logout can wait while the editor remains mounted.
    await writes.signOut(() => signOut(async () => {
      // Retire before the stored user goes: the protected boundary renders null in the same commit that sees
      // the user removed, so no protected view renders without a user. Its media close once it left.
      const published = spaces !== undefined
      realtime.closeAll()
      // :uc[sign-out] closes prior-principal query state: a refetch scheduled in this window would read the removed user.
      spaces?.clearReaders()
      // The writes gate flushed every open page before logout; an unload journal left behind leaves with the user.
      if (spaces) purgePageUnloads(storage, spaces.userId)
      const retiring = bucket
      spaces = undefined
      async function release() {
        await retiring?.close()
        if (bucket === retiring) bucket = undefined
      }
      // Initialization publishes the session exactly when it sets spaces; retire runs cleanups only for a published one.
      if (published) await session.retire(release)
      else await release()
    }))
  }

  let closing: Promise<void> | undefined
  return {
    get tel() {
      return flashcardsClientTel.current
    },
    queryClient,
    pageCommit: options?.pageCommit ?? defaultPageCommitPolicy,
    writes,
    adoptBumpErrors: createAdoptBumpErrors(queryClient),
    session,
    realtime: (workspaceId: string) => realtime.get(workspaceId),
    async closeWorkspace(workspaceId: string) { await writes.flush(); await spaces?.closeWorkspace(workspaceId) },
    rpc,
    rpcClient,
    auth: authClient,
    fetchFn,
    serverEmbeddings,
    searchEmbeddings,
    close() {
      if (closing) return closing
      queryClient.getQuery(rpc.workspace.list.queryOptions().queryKey)?.destroy()
      initializingSpaces?.cancel()
      return closing ??= disposeAll([
        () => { window.removeEventListener('focus', focus); window.removeEventListener('online', focus); stopVisibility() },
        () => { realtime.closeAll() },
        () => initialization,
        () => writes.flush(),
        () => { spaces?.cancel() },
        () => { spaces = undefined; return session.closeAll() },
        () => searchEmbeddings.close(),
        () => { serverEmbeddings.close() },
        async () => { await bucket?.close(); bucket = undefined },
        () => { queryClient.destroy() },
      ], 'Flashcards shutdown failed')
    },
    localStorage: storage,
    deviceId,
    testSupport: device.testSupport,
    clock: systemClock,
    defaultNativeLanguage: preferredBrowserLanguage(),
    speechStore: createDeviceSpeechStore(),
    initFlags: new Set(),
    getUser: () => {
      throw new Error('Not authenticated')
    },
    _ensureUserResources,
    moveSubtree(input: MoveInput) {
      return movePageCommand(this, input)
    },
    get spaces() { return requireSpaces() },
    db: (spaceId: string) => requireSpaces().db(spaceId),
    customDb: (spaceId: string) => requireSpaces().customDb(spaceId),
    async openWorkspace(workspaceId: string) { await requireSpaces().openWorkspace(workspaceId, tel) },
    afterCommit: (spaceId: string) => requireSpaces().afterCommit(spaceId),
    afterLocalCommit: async (spaceId: string): Promise<void> => { await requireSpaces().afterLocalCommit(spaceId) },
    embeddings: (spaceId: string) => requireSpaces().get(spaceId).embeddings,
    async afterDirectoryCommand() { await refreshOpenWorkspaces() },
    get bucket() { return mediaClient() },
    frequencyList: createStaticFrequencyList(globalThis.fetch.bind(globalThis)),
  } satisfies FlashcardsFeRouterCtx
}

export function preferredBrowserLanguage(): string {
  return typeof navigator.language === 'string'
    ? (navigator.language.split('-')[0] ?? 'en')
    : 'en'
}
